import { useCallback, useEffect, useRef, useState } from 'react';

export interface SuggestionIgn {
  libelle: string;
  numero: string;
  rue: string;
  codePostal: string;
  ville: string;
}

// Le libelle IGN d'un numero est « <numero> <rue>, <code postal> <ville> » : le numero est ce qui
// precede la rue, complement compris (« 8 bis »), et non le premier mot seul.
const numeroDepuisLibelle = (libelle: string, rue: string): string => {
  const position = rue ? libelle.indexOf(rue) : -1;
  return (position > 0 ? libelle.slice(0, position) : libelle.split(' ')[0]).trim();
};

const versSuggestion = (resultat: Record<string, unknown>): SuggestionIgn => {
  const libelle = String(resultat.fulltext ?? '');
  const rue = String(resultat.street ?? '');
  return {
    libelle,
    numero: resultat.kind === 'housenumber' ? numeroDepuisLibelle(libelle, rue) : '',
    rue,
    codePostal: String(resultat.zipcode ?? ''),
    ville: String(resultat.city ?? ''),
  };
};

/**
 * Auto-completion d'adresse sur le geocodeur IGN (territoire francais uniquement).
 *
 * La recherche ne part que sur appel explicite de `rechercher`, jamais sur un effet lie a la
 * valeur du champ : une adresse posee par le code (SIRENE, choix dans la liste) ne doit pas
 * rouvrir la liste et reclamer un clic de plus.
 */
export const useRechercheAdresseIgn = () => {
  const [suggestions, setSuggestions] = useState<SuggestionIgn[]>([]);
  const [chargement, setChargement] = useState(false);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controleur = useRef<AbortController | null>(null);

  const annuler = useCallback(() => {
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = null;
    controleur.current?.abort();
    controleur.current = null;
  }, []);

  const vider = useCallback(() => {
    annuler();
    setSuggestions([]);
    setChargement(false);
  }, [annuler]);

  const rechercher = useCallback((texte: string) => {
    annuler();
    const requete = texte.trim();
    if (requete.length < 3) {
      setSuggestions([]);
      setChargement(false);
      return;
    }
    setChargement(true);
    minuteur.current = setTimeout(async () => {
      const ctrl = new AbortController();
      controleur.current = ctrl;
      try {
        const reponse = await fetch(
          `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(requete)}&index=StreetAddress&limit=8`,
          { signal: ctrl.signal },
        );
        const json = reponse.ok ? await reponse.json() : null;
        if (ctrl.signal.aborted) return;
        setSuggestions(Array.isArray(json?.results) ? json.results.map(versSuggestion) : []);
      } catch {
        if (!ctrl.signal.aborted) setSuggestions([]);
      } finally {
        if (!ctrl.signal.aborted) setChargement(false);
      }
    }, 300);
  }, [annuler]);

  useEffect(() => annuler, [annuler]);

  return { suggestions, chargement, rechercher, vider };
};
